
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface SavedPrompt {
  id: string;
  prompt: {
    id: string;
    title: string;
    description: string;
    content: string;
    category: {
      name: string;
    };
    user_id?: string;
    author_name?: string;
  };
  saved_at: string;
  custom_title?: string;
  notes?: string;
  is_favorite?: boolean;
}

export interface PromptCollection {
  id: string;
  name: string;
  description?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  prompts_count: number;
}

export interface UserTag {
  id: string;
  user_id: string;
  name: string;
  color: string | null;
  created_at: string;
}

export const savePrompt = async (promptId: string, customTitle?: string) => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      toast.error("You must be logged in to save prompts");
      return null;
    }
    
    const userId = user.user.id;
    
    // Check if the prompt is already saved
    const { data: existingSaved, error: checkError } = await supabase
      .from("user_saved_prompts")
      .select("id")
      .eq("user_id", userId)
      .eq("prompt_id", promptId)
      .maybeSingle();
      
    if (checkError) {
      console.error("Error checking if prompt is saved:", checkError);
      throw checkError;
    }
    
    if (existingSaved) {
      console.log("Prompt already saved with ID:", existingSaved.id);
      
      // For toggle functionality - remove the saved prompt
      const { error: deleteError } = await supabase
        .from("user_saved_prompts")
        .delete()
        .eq("id", existingSaved.id);
        
      if (deleteError) {
        console.error("Error removing saved prompt:", deleteError);
        throw deleteError;
      }
      
      toast.success("Prompt removed from your library");
      return null;
    }
    
    // Insert new saved prompt
    const { data, error } = await supabase
      .from("user_saved_prompts")
      .insert({
        prompt_id: promptId,
        user_id: userId,
        custom_title: customTitle || null
      })
      .select("id")
      .single();
      
    if (error) {
      console.error("Error saving prompt:", error);
      throw error;
    }
    
    console.log("Prompt saved successfully with ID:", data.id);
    
    // Check if user has any collections, and create a default one if not
    const { count, error: countError } = await supabase
      .from("prompt_collections")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);
      
    if (countError) {
      console.error("Error checking collections:", countError);
    } else if (count === 0) {
      await createCollection("General", "Your default collection");
      console.log("Created General collection for first-time user");
    }
    
    toast.success("Prompt added to your library");
    
    return data.id;
  } catch (error: any) {
    console.error("Error saving prompt:", error);
    toast.error(error.message || "An error occurred while saving the prompt");
    return null;
  }
};

export const unsavePrompt = async (savedPromptId: string) => {
  try {
    const { error } = await supabase
      .from("user_saved_prompts")
      .delete()
      .eq("id", savedPromptId);
      
    if (error) throw error;
    
    toast.success("Prompt removed from your library");
    
    return true;
  } catch (error: any) {
    console.error("Error removing saved prompt:", error);
    toast.error(error.message || "An error occurred while removing the prompt");
    return false;
  }
};

export const getSavedPrompts = async (includeCollections = true): Promise<SavedPrompt[]> => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      console.log("No user logged in for getSavedPrompts");
      return [];
    }

    console.log("Getting saved prompts for user ID:", user.user.id);

    // Get all saved prompts for the current user
    const { data: savedPromptsData, error: savedPromptsError } = await supabase
      .from('user_saved_prompts')
      .select(`
        id,
        saved_at,
        custom_title,
        notes,
        is_favorite,
        prompt_id
      `)
      .eq('user_id', user.user.id)
      .order('saved_at', { ascending: false });

    if (savedPromptsError) {
      console.error('Error fetching saved prompts:', savedPromptsError);
      toast.error("Failed to load saved prompts");
      return [];
    }

    if (!savedPromptsData || savedPromptsData.length === 0) {
      console.log("No saved prompts found for user");
      return [];
    }

    console.log(`Found ${savedPromptsData.length} saved prompts with IDs:`, savedPromptsData.map(sp => sp.id));
    console.log(`Prompt IDs to fetch:`, savedPromptsData.map(sp => sp.prompt_id));
    
    // Get the details of all prompts that were saved
    const promptIds = savedPromptsData.map(sp => sp.prompt_id);
    const { data: promptsData, error: promptsError } = await supabase
      .from('prompts')
      .select(`
        id,
        title,
        description,
        content,
        user_id,
        category_id,
        categories(name)
      `)
      .in('id', promptIds);

    if (promptsError) {
      console.error('Error fetching prompt details:', promptsError);
      toast.error("Failed to load prompt details");
      return [];
    }

    console.log(`Retrieved ${promptsData?.length || 0} prompt details out of ${promptIds.length} requested`);
    if (promptsData?.length !== promptIds.length) {
      console.warn("Some prompt details were not found:", 
        promptIds.filter(id => !promptsData?.find(p => p.id === id)));
    }

    // Get user names for prompt authors
    const authorIds = promptsData
      ?.map(prompt => prompt.user_id)
      .filter(Boolean) as string[] || [];
    
    const { data: authorsData, error: authorsError } = await supabase
      .from('profiles')
      .select(`
        id,
        first_name,
        last_name
      `)
      .in('id', authorIds);
    
    if (authorsError) {
      console.error('Error fetching author details:', authorsError);
    }
    
    console.log(`Retrieved ${authorsData?.length || 0} author details`);

    // Create a map of user_id to author name
    const authorMap = new Map();
    if (authorsData && authorsData.length > 0) {
      authorsData.forEach(author => {
        const firstName = author.first_name || '';
        const lastName = author.last_name || '';
        const fullName = firstName || lastName ? `${firstName} ${lastName}`.trim() : 'Unknown';
        authorMap.set(author.id, fullName);
      });
    }

    // Combine saved prompt data with prompt details
    const savedPrompts: SavedPrompt[] = savedPromptsData.map(sp => {
      const promptData = promptsData?.find(p => p.id === sp.prompt_id);
      
      if (!promptData) {
        console.warn(`Prompt data not found for saved prompt ID: ${sp.id}, prompt_id: ${sp.prompt_id}`);
        return null;
      }

      return {
        id: sp.id,
        prompt: {
          id: promptData.id,
          title: promptData.title || "Unknown Title",
          description: promptData.description || "",
          content: promptData.content || "",
          category: {
            name: promptData.categories?.name || 'Uncategorized'
          },
          user_id: promptData.user_id || '',
          author_name: authorMap.get(promptData.user_id) || 'Unknown'
        },
        saved_at: sp.saved_at,
        custom_title: sp.custom_title,
        notes: sp.notes,
        is_favorite: sp.is_favorite
      };
    }).filter(Boolean) as SavedPrompt[];

    console.log(`Processed ${savedPrompts.length} saved prompts successfully`);
    return savedPrompts;
  } catch (error) {
    console.error('Error in getSavedPrompts:', error);
    toast.error("An error occurred while loading your saved prompts");
    return [];
  }
};

export const updateSavedPrompt = async (
  savedPromptId: string, 
  updates: { 
    custom_title?: string; 
    notes?: string; 
    is_favorite?: boolean;
  }
) => {
  try {
    const { data, error } = await supabase
      .from("user_saved_prompts")
      .update(updates)
      .eq("id", savedPromptId)
      .select()
      .single();
      
    if (error) throw error;
    
    toast.success("Your saved prompt has been updated");
    
    return data;
  } catch (error: any) {
    console.error("Error updating saved prompt:", error);
    toast.error(error.message || "An error occurred while updating the prompt");
    return null;
  }
};

export const getPromptCollections = async () => {
  try {
    const { data, error } = await supabase
      .from("prompt_collections")
      .select(`
        id,
        user_id,
        name,
        description,
        created_at,
        updated_at
      `)
      .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
      .order("name");
      
    if (error) throw error;
    
    const collectionsWithCount = await Promise.all(
      data.map(async (collection) => {
        const { count, error: countError } = await supabase
          .from("collection_prompts")
          .select("id", { count: "exact" })
          .eq("collection_id", collection.id);
          
        if (countError) {
          console.error("Error counting prompts in collection:", countError);
          return { ...collection, prompts_count: 0 };
        }
        
        return { ...collection, prompts_count: count };
      })
    );
    
    return collectionsWithCount as PromptCollection[];
  } catch (error: any) {
    console.error("Error fetching collections:", error);
    toast.error(error.message || "An error occurred while fetching your collections");
    return [];
  }
};

export const createCollection = async (name: string, description?: string) => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      toast.error("You must be logged in to create collections");
      return null;
    }
    
    const { data, error } = await supabase
      .from("prompt_collections")
      .insert({
        name,
        description: description || null,
        user_id: user.user.id
      })
      .select()
      .single();
      
    if (error) {
      console.error("Error creating collection:", error);
      throw error;
    }
    
    toast.success("New collection created");
    
    const newCollection: PromptCollection = {
      ...data,
      prompts_count: 0
    };
    
    return newCollection;
  } catch (error: any) {
    console.error("Error creating collection:", error);
    toast.error(error.message || "An error occurred while creating the collection");
    return null;
  }
};

export const updateCollection = async (
  collectionId: string, 
  updates: { name?: string; description?: string }
) => {
  try {
    const { data, error } = await supabase
      .from("prompt_collections")
      .update(updates)
      .eq("id", collectionId)
      .select()
      .single();
      
    if (error) throw error;
    
    toast.success("Collection has been updated successfully");
    
    const { data: collectionData } = await supabase
      .from("prompt_collections")
      .select(`
        id,
        user_id,
        name,
        description,
        created_at,
        updated_at
      `)
      .eq("id", collectionId)
      .single();
      
    const { count } = await supabase
      .from("collection_prompts")
      .select("id", { count: "exact" })
      .eq("collection_id", collectionId);
    
    return { 
      ...data,
      prompts_count: count || 0
    } as PromptCollection;
  } catch (error: any) {
    console.error("Error updating collection:", error);
    toast.error(error.message || "An error occurred while updating the collection");
    return null;
  }
};

export const deleteCollection = async (collectionId: string) => {
  try {
    const { error } = await supabase
      .from("prompt_collections")
      .delete()
      .eq("id", collectionId);
      
    if (error) throw error;
    
    toast.success("Collection has been deleted successfully");
    
    return true;
  } catch (error: any) {
    console.error("Error deleting collection:", error);
    toast.error(error.message || "An error occurred while deleting the collection");
    return false;
  }
};

export const getCollectionPrompts = async (collectionId: string) => {
  try {
    const { data, error } = await supabase
      .from("collection_prompts")
      .select(`
        id,
        collection_id,
        added_at,
        saved_prompt:saved_prompt_id(
          id,
          user_id,
          prompt_id,
          saved_at,
          custom_title,
          notes,
          is_favorite,
          prompt:prompts(
            id, 
            title, 
            description, 
            content,
            category:categories(name)
          )
        )
      `)
      .eq("collection_id", collectionId)
      .order("added_at", { ascending: false });
      
    if (error) throw error;
    
    return data.map(item => item.saved_prompt) as SavedPrompt[];
  } catch (error: any) {
    console.error("Error fetching collection prompts:", error);
    toast.error(error.message || "An error occurred while fetching collection prompts");
    return [];
  }
};

export const addPromptToCollection = async (promptId: string, collectionId: string) => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      toast.error("You must be logged in to add to collections");
      return null;
    }
    
    console.log(`Adding prompt ${promptId} to collection ${collectionId}`);
    
    let savedPromptId = null;
    const { data: existingSaved, error: checkSavedError } = await supabase
      .from("user_saved_prompts")
      .select("id")
      .eq("user_id", user.user.id)
      .eq("prompt_id", promptId)
      .maybeSingle();
    
    if (checkSavedError) {
      console.error("Error checking if prompt is saved:", checkSavedError);
      throw checkSavedError;
    }
    
    if (existingSaved) {
      console.log("Using existing saved prompt:", existingSaved.id);
      savedPromptId = existingSaved.id;
    } else {
      // Insert new saved prompt
      const { data, error } = await supabase
        .from("user_saved_prompts")
        .insert({
          prompt_id: promptId,
          user_id: user.user.id,
        })
        .select("id")
        .single();
        
      if (error) {
        console.error("Error saving prompt:", error);
        throw error;
      }
      
      console.log("New saved prompt created with ID:", data.id);
      savedPromptId = data.id;
    }
    
    // Check if prompt is already in this collection
    const { data: existing, error: checkError } = await supabase
      .from("collection_prompts")
      .select("id")
      .eq("collection_id", collectionId)
      .eq("saved_prompt_id", savedPromptId)
      .maybeSingle();
      
    if (checkError) {
      console.error("Error checking if prompt is in collection:", checkError);
      throw checkError;
    }
    
    if (existing) {
      console.log("Prompt already in collection:", existing.id);
      toast.success("This prompt is already in the selected collection");
      return existing.id;
    }
    
    // Add to collection
    const { data, error } = await supabase
      .from("collection_prompts")
      .insert({
        collection_id: collectionId,
        saved_prompt_id: savedPromptId
      })
      .select()
      .single();
      
    if (error) {
      console.error("Database error adding to collection:", error);
      throw error;
    }
    
    console.log("Added to collection successfully:", data.id);
    
    toast.success("Prompt added to collection");
    
    return data.id;
  } catch (error: any) {
    console.error("Error adding prompt to collection:", error);
    toast.error(error.message || "An error occurred while adding prompt to collection");
    return null;
  }
};

export const removePromptFromCollection = async (collectionPromptId: string) => {
  try {
    const { error } = await supabase
      .from("collection_prompts")
      .delete()
      .eq("id", collectionPromptId);
      
    if (error) throw error;
    
    toast.success("Prompt removed from collection successfully");
    
    return true;
  } catch (error: any) {
    console.error("Error removing prompt from collection:", error);
    toast.error(error.message || "An error occurred while removing prompt from collection");
    return false;
  }
};

export const getUserTags = async () => {
  try {
    const { data, error } = await supabase
      .from("user_tags")
      .select()
      .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
      .order("name");
      
    if (error) throw error;
    
    return data as UserTag[];
  } catch (error: any) {
    console.error("Error fetching user tags:", error);
    toast.error(error.message || "An error occurred while fetching your tags");
    return [];
  }
};

export const createTag = async (name: string, color?: string) => {
  try {
    const { data, error } = await supabase
      .from("user_tags")
      .insert({
        name,
        color: color || null,
        user_id: (await supabase.auth.getUser()).data.user?.id
      })
      .select()
      .single();
      
    if (error) throw error;
    
    toast.success("New tag created successfully");
    
    return data;
  } catch (error: any) {
    console.error("Error creating tag:", error);
    toast.error(error.message || "An error occurred while creating the tag");
    return null;
  }
};

export const addTagToPrompt = async (savedPromptId: string, tagId: string) => {
  try {
    const { data: existing, error: checkError } = await supabase
      .from("prompt_tags")
      .select("id")
      .eq("saved_prompt_id", savedPromptId)
      .eq("tag_id", tagId)
      .maybeSingle();
      
    if (checkError) throw checkError;
    
    if (existing) {
      toast.success("This tag is already applied to the prompt");
      return existing.id;
    }
    
    const { data, error } = await supabase
      .from("prompt_tags")
      .insert({
        saved_prompt_id: savedPromptId,
        tag_id: tagId
      })
      .select()
      .single();
      
    if (error) throw error;
    
    toast.success("Tag applied to prompt successfully");
    
    return data.id;
  } catch (error: any) {
    console.error("Error applying tag:", error);
    toast.error(error.message || "An error occurred while applying the tag");
    return null;
  }
};

export const removeTagFromPrompt = async (promptTagId: string) => {
  try {
    const { error } = await supabase
      .from("prompt_tags")
      .delete()
      .eq("id", promptTagId);
      
    if (error) throw error;
    
    toast.success("Tag removed from prompt successfully");
    
    return true;
  } catch (error: any) {
    console.error("Error removing tag:", error);
    toast.error(error.message || "An error occurred while removing the tag");
    return false;
  }
};

export const getPromptTags = async (savedPromptId: string) => {
  try {
    const { data, error } = await supabase
      .from("prompt_tags")
      .select(`
        id,
        saved_prompt_id,
        added_at,
        tag:tag_id(
          id,
          name,
          color
        )
      `)
      .eq("saved_prompt_id", savedPromptId);
      
    if (error) throw error;
    
    return data.map(item => ({
      id: item.id,
      saved_prompt_id: item.saved_prompt_id,
      added_at: item.added_at,
      tag: item.tag
    }));
  } catch (error: any) {
    console.error("Error fetching prompt tags:", error);
    toast.error(error.message || "An error occurred while fetching prompt tags");
    return [];
  }
};
